import { createContext, useState, useContext, useEffect, useRef } from "react";
import { useAuthentication } from "../../../contexts/AuthContext";
import * as signalR from "@microsoft/signalr";
import { signalRConfig } from "../../../configs/signalRConfig";
import { debugLog } from "../../../configs/loggingConfig";

const SignalRContext = createContext();

const DEFAULT_TRANSPORTS =
    signalR.HttpTransportType.WebSockets |
    signalR.HttpTransportType.ServerSentEvents |
    signalR.HttpTransportType.LongPolling;

const transportMap = {
    websocket: signalR.HttpTransportType.WebSockets,
    websockets: signalR.HttpTransportType.WebSockets,
    webSockets: signalR.HttpTransportType.WebSockets,
    sse: signalR.HttpTransportType.ServerSentEvents,
    serverSentEvents: signalR.HttpTransportType.ServerSentEvents,
    serverSentEvent: signalR.HttpTransportType.ServerSentEvents,
    longPolling: signalR.HttpTransportType.LongPolling,
    longpolling: signalR.HttpTransportType.LongPolling,
};

const getConfiguredTransports = () => {
    if (!signalRConfig.overrideConnection) return DEFAULT_TRANSPORTS;

    if (typeof signalRConfig.overrideConnection === "number") {
        return signalRConfig.overrideConnection;
    }

    return `${signalRConfig.overrideConnection}`
        .split(/[\s,|]+/)
        .filter(Boolean)
        .reduce((transports, value) => transports | (transportMap[value] ?? 0), 0) || DEFAULT_TRANSPORTS;
};

function SignalRProvider({ children }) {
    const { apiAccessToken } = useAuthentication();

    const [mappingMethods, setMappingMethods] = useState([]);
    const [hubConnect, setHubConnect] = useState(null);
    const mappedNamesRef = useRef(new Set());

    useEffect(() => {
        if (!apiAccessToken) return;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(`${signalRConfig.baseHubUrl}communications`, {
                accessTokenFactory: () => apiAccessToken,
                transport: getConfiguredTransports(),
            })
            .withAutomaticReconnect()
            .build();

        mappedNamesRef.current = new Set();
        setHubConnect(connection);

        return () => {
            mappedNamesRef.current = new Set();
            connection.stop().catch((error) => {
                debugLog.log("[SIGR] Error while stopping previous SignalR connection " + error.message);
            });
        };
    }, [apiAccessToken]);

    useEffect(() => {
        if (!hubConnect) return;
        connectSignalR();
    }, [hubConnect]);

    useEffect(() => {
        if (!hubConnect) return;
        mapMethods();
    }, [mappingMethods, hubConnect]);

    const connectSignalR = async () => {
        if (!hubConnect) return;

        if (
            hubConnect.state === signalR.HubConnectionState.Connected ||
            hubConnect.state === signalR.HubConnectionState.Connecting
        ) {
            return;
        }

        try {
            debugLog.log(`[SIGR] Attempting to connect SignalR with transports: ${getConfiguredTransports()}`);
            await hubConnect.start();
            debugLog.log("[SIGR] Connection successful!");
            mapMethods();
        } catch (error) {
            debugLog.log(
                "[SIGR] Error while establishing connection with the SignalRChatHub " +
                error.message
            );
        }
    };

    const addMappingMethod = (mapping) => {
        if (!mapping?.TextName || typeof mapping?.Method !== "function") return;

        setMappingMethods((prev) => {
            if (prev.some((x) => x?.TextName === mapping.TextName)) return prev;
            return [...prev, mapping];
        });
    };

    const mapMethods = () => {
        mappingMethods.forEach((methodDef) => {
            if (
                methodDef &&
                typeof methodDef?.TextName === "string" &&
                typeof methodDef?.Method === "function"
            ) {
                if (mappedNamesRef.current.has(methodDef.TextName)) return;

                hubConnect?.on(methodDef?.TextName, (param1) => {
                    debugLog.log(`[SIGNALR] Received SignalR notification mapped to ${methodDef?.TextName}`);
                    let parsedMessage = JSON.parse(param1);
                    methodDef.Method(parsedMessage);
                });

                mappedNamesRef.current.add(methodDef.TextName);
            } else {
                debugLog.log("[SIGNALR] Failed to map a method definition");
            }
        });
    };

    return (
        <SignalRContext.Provider
            value={{
                addMappingMethod,
                connectSignalR
            }}
        >
            {children}
        </SignalRContext.Provider>
    );
}

function useSignalR() {
    const context = useContext(SignalRContext);
    if (context === undefined) {
        throw new Error(
            "useSignalR must be used within a SignalRProvider"
        );
    }
    return context;
}

export { SignalRProvider, useSignalR };
